import { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useNotify } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import { apiErrorMessage } from "../shared/api-error";

type ClassOption = { id: string; name: string };
type SyncResult = {
  enrollments: number;
  create: number;
  update: number;
  cancel: number;
  unchanged: number;
  paidUntouched: number;
  conflicts: Array<{ enrollmentId: string; studentName: string; message: string }>;
  perEnrollment: Array<{
    enrollmentId: string;
    studentName: string;
    className: string;
    create: number;
    update: number;
    cancel: number;
    conflicts: number;
  }>;
};

const ALL = "ALL";

/**
 * Brings students' generated dues in line with the current fees. Always previews first; paid
 * dues are never changed and the dialog says how many were left alone.
 */
export function SyncDuesDialog({
  open,
  onClose,
  yearId,
  yearName,
  classes,
}: {
  open: boolean;
  onClose: () => void;
  yearId: string;
  yearName: string;
  classes: ClassOption[];
}) {
  const notify = useNotify();
  const [classId, setClassId] = useState(ALL);
  const [preview, setPreview] = useState<SyncResult | null>(null);
  const [applied, setApplied] = useState<SyncResult | null>(null);
  const [busy, setBusy] = useState<"preview" | "apply" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const body = () => ({ academicYearId: yearId, ...(classId !== ALL ? { classId } : {}) });
  const reset = () => {
    setPreview(null);
    setApplied(null);
    setError(null);
  };
  const close = () => {
    if (busy) return;
    onClose();
    reset();
    setClassId(ALL);
  };

  const run = async (mode: "preview" | "apply") => {
    setBusy(mode);
    setError(null);
    try {
      const response = await callApi({
        location: mode === "preview" ? "feeDues/sync-preview" : "feeDues/sync-apply",
        method: "POST",
        data: body(),
      });
      const data = response.data as SyncResult;
      if (mode === "preview") {
        setPreview(data);
        setApplied(null);
      } else {
        setApplied(data);
        setPreview(null);
        notify(
          `Dues synced for ${data.enrollments} ${data.enrollments === 1 ? "student" : "students"}.`,
          {
            type: "success",
          }
        );
      }
    } catch (err: unknown) {
      setError(
        apiErrorMessage(
          err,
          mode === "preview" ? "Could not check the dues" : "Could not sync the dues"
        )
      );
    } finally {
      setBusy(null);
    }
  };

  const shown = applied ?? preview;
  const changes = shown ? shown.create + shown.update + shown.cancel : 0;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md">
      <DialogTitle>Sync dues for {yearName}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Changing a fee does not change the dues already generated for students. Sync brings unpaid
          dues in line with the current fees. Dues that have been paid are never changed.
        </Alert>
        <TextField
          select
          label="Which students"
          value={classId}
          onChange={(e) => {
            setClassId(e.target.value);
            reset();
          }}
          disabled={busy !== null}
          sx={{ minWidth: 260, mb: 2 }}
        >
          <MenuItem value={ALL}>All classes</MenuItem>
          {classes.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {shown && (
          <Box>
            <Alert severity={applied ? "success" : changes ? "info" : "success"} sx={{ mb: 2 }}>
              {applied ? "Synced. " : "Preview: "}
              {shown.enrollments} {shown.enrollments === 1 ? "student" : "students"} checked.{" "}
              {changes
                ? `${shown.update} ${shown.update === 1 ? "due" : "dues"} ${applied ? "updated" : "to update"}, ${shown.create} ${applied ? "added" : "to add"}, ${shown.cancel} ${applied ? "cancelled" : "to cancel"}.`
                : "Everything is already up to date."}
              {shown.paidUntouched > 0 &&
                ` ${shown.paidUntouched} paid ${shown.paidUntouched === 1 ? "due is" : "dues are"} left unchanged.`}
            </Alert>
            {shown.conflicts.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                These dues were left as they are because the new fee is lower than what was already
                paid:
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {shown.conflicts.map((c, i) => (
                    <li key={`${c.enrollmentId}-${i}`}>
                      {c.studentName}: {c.message}
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
            {shown.perEnrollment.length > 0 && (
              <Box sx={{ maxHeight: 280, overflow: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Student</TableCell>
                      <TableCell>Class</TableCell>
                      <TableCell align="right">Update</TableCell>
                      <TableCell align="right">Add</TableCell>
                      <TableCell align="right">Cancel</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shown.perEnrollment.map((e) => (
                      <TableRow key={e.enrollmentId}>
                        <TableCell>{e.studentName}</TableCell>
                        <TableCell>{e.className}</TableCell>
                        <TableCell align="right">{e.update}</TableCell>
                        <TableCell align="right">{e.create}</TableCell>
                        <TableCell align="right">{e.cancel}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
            {shown.perEnrollment.length === 0 && changes === 0 && shown.conflicts.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Nothing to change.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy !== null}>
          Close
        </Button>
        <Button variant="outlined" onClick={() => run("preview")} disabled={busy !== null}>
          {busy === "preview" ? "Checking…" : "Preview"}
        </Button>
        <Button
          variant="contained"
          onClick={() => run("apply")}
          disabled={busy !== null || !preview || changes === 0}
        >
          {busy === "apply" ? "Syncing…" : `Apply ${changes || ""} changes`.trim()}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
